import { toStatusPresentation } from "./statusPresentation";

export type ReceiptTranslate = (
  key: string,
  options?: Record<string, unknown>
) => string;

export interface ReceiptRow {
  /** Stable so the screen can decorate specific rows (status). */
  id: string;
  label: string;
  value: string;
  emphasis?: boolean;
}

export interface CanonicalReference {
  type: string;
  value: string;
  label?: Record<string, string>;
  presentation: string;
  issuedAt?: string;
}

export const hidesReceiptSettlementCopy = (status: string | undefined): boolean => {
  const s = (status ?? "").toLowerCase();
  return s === "failed" || s === "cancelled" || s === "canceled";
};

/** Subset of the widget operation used by the receipt (screen + PDF). */
export interface ReceiptOperation {
  operationId: string;
  status: string;
  statusReason?: string;
  sendAmount: number;
  receiveAmount: number;
  exchangeRate: number;
  fee?: number;
  fundingFee?: number;
  transactionFee?: number;
  taxFee?: number;
  totalFee?: number;
  totalCost: number;
  sourceCurrency: string;
  destinationCurrency: string;
  recipient?: {
    name?: {
      firstName?: string;
      lastName?: string;
    };
  };
  references?: CanonicalReference[];
  createdAt: string;
}

export const formatReceiptDate = (date: Date): string => {
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const formatReceiptDateTime = (isoDate: string): string => {
  const date = new Date(isoDate);
  return `${formatReceiptDate(date)}\n${date.toLocaleTimeString()}`;
};

/** Funds settle on the 5th business day after the operation is created. */
export const availableReceiptDate = (isoDate: string): string => {
  const result = new Date(isoDate);
  let added = 0;
  while (added < 5) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return formatReceiptDate(result);
};

const formatAmount = (
  amount: number,
  _lang: string,
  digits?: { min?: number; max?: number }
): string =>
  new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: digits?.min ?? 2,
    maximumFractionDigits: digits?.max ?? 2,
  }).format(amount);

export const formatReceiptMoney = (
  amount: number | undefined,
  currency: string,
  lang = "en"
): string => {
  const formatted = formatAmount(amount ?? 0, lang);
  const code = currency.toUpperCase();
  return code === "USD" ? `$${formatted} ${code}` : `${formatted} ${code}`;
};

export const referenceLabel = (
  type: string,
  label: Record<string, string> | undefined,
  t: ReceiptTranslate,
  lang: string
): string => {
  const fromEnvelope = label?.[lang] || label?.en;
  if (fromEnvelope) return fromEnvelope;
  const key = `references.${type}`;
  const translated = t(key);
  if (translated !== key) return translated;
  return type;
};

export const splitVisibleReferences = (
  references: CanonicalReference[] | undefined
): { prominent: CanonicalReference[]; secondary: CanonicalReference[] } => {
  const prominent: CanonicalReference[] = [];
  const secondary: CanonicalReference[] = [];
  for (const ref of references ?? []) {
    if (ref.presentation === "INTERNAL") continue;
    if (ref.presentation === "PROMINENT") {
      prominent.push(ref);
    } else {
      secondary.push(ref);
    }
  }
  return { prominent, secondary };
};

/**
 * Single source of receipt rows for the on-screen card and the vector PDF.
 * Adding a row here updates both surfaces.
 */
export const buildReceiptRows = (
  operation: ReceiptOperation,
  t: ReceiptTranslate,
  opts: { lang: string }
): ReceiptRow[] => {
  const source = operation.sourceCurrency;
  const destination = operation.destinationCurrency;
  const receiverName = [
    operation.recipient?.name?.firstName,
    operation.recipient?.name?.lastName,
  ]
    .filter(Boolean)
    .join(" ");
  const { secondary } = splitVisibleReferences(operation.references);
  const { labelKey } = toStatusPresentation(operation.status);
  const money = (amount: number | undefined, currency: string) =>
    formatReceiptMoney(amount, currency, opts.lang);

  const rows: ReceiptRow[] = [
    {
      id: "status",
      label: t("receipt.status"),
      value: t(labelKey),
      emphasis: true,
    },
  ];

  if (operation.statusReason === "provider_rejected") {
    rows.push({
      id: "statusReason",
      label: t("receipt.statusReason"),
      value: t("receipt.statusReasonProviderRejected"),
    });
  }

  rows.push(
    {
      id: "transferAmount",
      label: t("receipt.transferAmount"),
      value: money(operation.sendAmount, source),
    },
    {
      id: "transactionFee",
      label: t("receipt.transactionFee"),
      value: money(operation.transactionFee, source),
    },
    {
      id: "exchangeRate",
      label: t("receipt.exchangeRate"),
      value: `${formatAmount(operation.exchangeRate, opts.lang, {
        min: 4,
        max: 4,
      })} ${destination}/${source}`,
    },
    {
      id: "totalPayment",
      label: t("receipt.totalPayment"),
      value: money(operation.totalCost, source),
    },
    {
      id: "receiving",
      label: t("receipt.receiving"),
      value: money(operation.receiveAmount, destination),
    },
    {
      id: "fundingFee",
      label: t("receipt.fundingFee"),
      value: money(
        operation.fundingFee ?? operation.fee ?? operation.totalFee,
        source
      ),
    },
    {
      id: "transferTaxes",
      label: t("receipt.transferTaxes"),
      value: money(operation.taxFee, source),
    },
  );

  if (receiverName) {
    rows.push({
      id: "receiver",
      label: t("receipt.receiver"),
      value: receiverName,
    });
  }

  rows.push({
    id: "transactionDate",
    label: t("receipt.transactionDate"),
    value: formatReceiptDateTime(operation.createdAt),
  });

  if (!hidesReceiptSettlementCopy(operation.status)) {
    rows.push({
      id: "dateAvailable",
      label: t("receipt.dateAvailable"),
      value: availableReceiptDate(operation.createdAt),
    });
  }

  for (const ref of secondary) {
    rows.push({
      id: `ref:${ref.type}`,
      label: referenceLabel(ref.type, ref.label, t, opts.lang),
      value: ref.value,
    });
  }

  return rows;
};
